import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { springConfig, authThemeConfigs, type AuthTheme } from './constants';

interface AnimatedButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'default' | 'lg';
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  theme?: AuthTheme;
}

export function AnimatedButton({
  children,
  variant = 'primary',
  size = 'default',
  loading = false,
  icon,
  iconPosition = 'right',
  className,
  disabled,
  theme = 'invol',
  ...props
}: AnimatedButtonProps) {
  const isDisabled = disabled || loading;
  const themeConfig = authThemeConfigs[theme] || authThemeConfigs.invol;

  // Theme-aware primary button styles
  const primaryStyles = cn(
    themeConfig.buttonPrimary,
    `shadow-lg ${themeConfig.buttonShadow}`,
    theme === 'invol'
      ? 'hover:shadow-blue-500/30'
      : 'hover:shadow-yellow-500/30',
    'disabled:from-slate-300 disabled:to-slate-400 disabled:shadow-none disabled:text-slate-500'
  );

  const variantStyles = {
    primary: primaryStyles,
    secondary: cn(
      'bg-white text-slate-700 border border-slate-200',
      'shadow-sm',
      'hover:bg-slate-50 hover:border-slate-300',
      'disabled:bg-slate-100 disabled:text-slate-400'
    ),
    ghost: cn(
      'bg-transparent text-slate-600',
      'hover:bg-slate-100 hover:text-slate-900',
      'disabled:text-slate-400'
    ),
  };

  const sizeStyles = {
    default: 'h-11 px-5 text-sm',
    lg: 'h-12 px-6 text-base',
  };

  // Theme-aware focus ring
  const focusRing = theme === 'drboese'
    ? 'focus:ring-yellow-500/20'
    : 'focus:ring-blue-500/20';

  return (
    <motion.button
      {...props}
      disabled={isDisabled}
      whileHover={isDisabled ? {} : { scale: 1.02 }}
      whileTap={isDisabled ? {} : { scale: 0.98 }}
      transition={springConfig.stiff}
      className={cn(
        'relative inline-flex items-center justify-center gap-2',
        'rounded-xl font-medium',
        'transition-all duration-200',
        `focus:outline-none focus:ring-2 ${focusRing} focus:ring-offset-2`,
        'disabled:cursor-not-allowed',
        variantStyles[variant],
        sizeStyles[size],
        className
      )}
    >
      {loading ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute inset-0 flex items-center justify-center"
        >
          <Loader2 className="h-5 w-5 animate-spin" />
        </motion.div>
      ) : null}

      <span
        className={cn(
          'flex items-center gap-2 transition-opacity',
          loading && 'opacity-0'
        )}
      >
        {iconPosition === 'left' && icon}
        {children}
        {iconPosition === 'right' && icon}
      </span>
    </motion.button>
  );
}
